// Thin API client — modelled on Hedge-Fund-Tracking-System apps/web/src/lib/api.ts.
//
// - Always sends cookies (`credentials: 'include'`)
// - Dev override attaches X-Dev-User-Id / X-Dev-Role headers when
//   VITE_DEV_USER_ID + VITE_DEV_ROLE are set in the dashboard's .env
// - On 401, redirects to VITE_AUTH_REDIRECT_URL when configured;
//   otherwise the error bubbles to the caller (caught by RouteGuard).

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

const DEV_USER_ID = import.meta.env.VITE_DEV_USER_ID;
const DEV_ROLE = import.meta.env.VITE_DEV_ROLE;
const AUTH_REDIRECT_URL = import.meta.env.VITE_AUTH_REDIRECT_URL;

function getHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  };
  if (DEV_USER_ID && DEV_ROLE) {
    headers['x-dev-user-id'] = DEV_USER_ID;
    headers['x-dev-role'] = DEV_ROLE;
  }
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...getHeaders(),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    if (res.status === 401 && typeof window !== 'undefined' && AUTH_REDIRECT_URL) {
      window.location.href = AUTH_REDIRECT_URL;
      return new Promise(() => {}); // never resolves — page is redirecting
    }
    const body = await res.text().catch(() => '');
    throw new Error(`API ${init?.method ?? 'GET'} ${path} → ${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
};

// ── Typed shapes (kept in sync by hand with src/dashboard/server.ts) ──

export interface SnapshotRow {
  id: number;
  ts: number;
  ethPrice: number | null;
  lpEth: number | null;
  lpUsdc: number | null;
  /** Live pending swap fees on the LP (added in 0002). */
  lpFeesPendingEth?: number | null;
  lpFeesPendingUsdc?: number | null;
  lpFeesPendingUsd?: number | null;
  perpShortEth: number | null;
  perpMargin: number | null;
  /** Full HL equity = margin + unrealized PnL (added in 0002). */
  hlAccountValue?: number | null;
  /** HL spot USDC, usable as perp collateral under unified-account (added in 0003). */
  hlSpotUsdc?: number | null;
  /** Phase 8: HL position's unrealized PnL (signed). */
  hlUnrealizedPnl?: number | null;
  /** HL MARK price for the hedge coin at this tick (added in 0019). Compared
   *  against ethPrice (the DEX pool price) to surface the cross-venue basis. */
  hlMarkPrice?: number | null;
  /** Phase 8: AERO accrued in gauge, not yet claimed. */
  aeroPendingTokens?: number | null;
  aeroPendingUsd?: number | null;
  /** Wallet balances (added in 0002). */
  walletEth?: number | null;
  walletWeth?: number | null;
  walletUsdc?: number | null;
  walletAero?: number | null;
  driftBps: number | null;
  pnlUsd: number | null;
  /** Full MTM NAV (persisted from 0002 onward, derived server-side for older rows). */
  navUsd?: number | null;
  /** Per-strategy NAV (Phase 3+): LP + fees + HL perp + cumulative AERO − cumulative gas.
   *  Excludes wallet residuals and HL spot. Growth baseline = strategies.deployed_capital_usd. */
  strategyNavUsd?: number | null;
}

export interface ActionRow {
  id: number;
  ts: number;
  kind: string;
  status: string;
  payload: Record<string, unknown>;
  txHash: string | null;
  error: string | null;
  /** Phase 4: NAV at action submit / next snapshot, and the breakdown. */
  navBeforeUsd?: number | null;
  navAfterUsd?: number | null;
  attribution?: Record<string, unknown> | null;
  /** Phase 4 (already present on bot side, expose for action history): */
  gasCostUsd?: number | null;
}

export interface Counts {
  snapshotCount: number;
  actionCount: number;
  actionFailures24h: number;
  autoHedgeCount24h: number;
  autoHarvestCount24h: number;
  autoRedeployCount24h: number;
}

export interface State {
  serverTs: number;
  bootTs: number;
  uptimeMs: number;
  lastTickTs: number;
  lastTickAgeMs: number | null;
  mode: {
    features: ('hedge' | 'redeploy' | 'harvest')[];
    dryRun: boolean;
    hlNetwork: 'mainnet' | 'testnet';
    authEnabled?: boolean;
  };
  thresholds: {
    driftBps: number;
    hlMinNotionalUsd: number;
    hlMinNotionalBufferBps: number;
    effectiveMinNotionalUsd: number;
    lpRangePct: number;
    hedgeCooldownSec: number;
    redeployCooldownSec: number;
    harvestCooldownSec: number;
  };
  counts: Counts;
  position: {
    poolAddress: string | null;
    activeNftId: string | null;
    hlUserAddress: string | null;
    hlCoin: string;
  };
  /** Phase 6 follow-up: all active strategies the user can switch to.
   *  Sorted by id ASC. The header dropdown renders one item per row. */
  availableStrategies?: {
    id: number;
    slug: string;
    displayName: string;
    status: string;
    mode: 'live' | 'paper';
    /** 'continuous' | 'static' — surfaced in switcher as a badge. */
    hedgeModel?: 'continuous' | 'static';
    /** Per-strategy opt-in for the atomic on-chain zap redeploy path. */
    useZapRedeploy?: boolean;
    venue?: 'aerodrome' | 'uniswap_v3';
  }[];
  /** First snapshot with LP data, used as the strategy's NAV baseline. */
  inception: {
    ts: number;
    navUsd: number | null;
  } | null;
  /** Legacy total-wallet NAV vs inception snapshot. Includes idle-balance
   *  price drift — useful for "wallet trend" but pollutes with non-strategy
   *  moves. The `strategy` block below is the honest growth metric. */
  nav: {
    currentUsd: number | null;
    inceptionUsd: number | null;
    growthUsd: number | null;
    growthPct: number | null;
  };
  /** Phase 3+: per-strategy NAV + growth, anchored to deployed_capital_usd. */
  strategy: {
    id: number;
    slug: string;
    displayName: string;
    status: string;
    /** Phase 7: 'live' or 'paper'. */
    mode?: 'live' | 'paper';
    /** Path 2: NAV at deployment time (LP + HL allocation). Growth
     *  baseline. Differs from budgetUsdc — see comments in
     *  server.ts:buildState. */
    deployedCapitalUsd: number | null;
    /** Path 2: operator's USDC budget at strategy creation. Fixed for
     *  the strategy's lifetime; shown alongside deployedCapitalUsd so
     *  the operator can see both "what I committed" and "what's actually
     *  on-venue". Null for legacy pre-Path-2 strategies. */
    budgetUsdc: number | null;
    /** Cumulative redeploy/deploy remainders refunded to the wallet and
     *  added back into NAV (migration 0021). Shown as its own leg in the
     *  economics breakdown so it isn't lumped into "unattributed". */
    dustUsd?: number;
    currentNavUsd: number | null;
    growthUsd: number | null;
    growthPct: number | null;
    /** Phase 6: needed by Operations panel to gate mint vs redeploy. */
    positionNftId: string | null;
    rangePct: number;
    /** DEX venue — gates AERO/harvest tiles ('uniswap_v3' has no gauge). */
    venue?: 'aerodrome' | 'uniswap_v3';
    poolFee?: number | null;
    /** Volatile-side display symbol: 'ETH', 'VIRTUAL', … */
    baseSymbol?: string;
    /** Phase 7: HODL baseline + IL. Null until first LP observation. */
    hodlBaselineEth?: number | null;
    hodlBaselineUsdc?: number | null;
    hodlValueUsd?: number | null;
    /** LP value − HODL value. Negative under typical IL conditions. */
    ilUsd?: number | null;
    ilPct?: number | null;
    /** Phase 8: HL position's unrealized PnL (signed, in USD). */
    hlUnrealizedPnl?: number | null;
    /** Phase 8: AERO accrued in the gauge but not yet harvested. */
    aeroPendingTokens?: number | null;
    /** Phase 8: USD value of pending AERO at current v2 spot quote. */
    aeroPendingUsd?: number | null;
  } | null;
  /** Wallet+HL spot value NOT committed to any strategy.
   *   operationalReserve = total_nav − strategy_nav. */
  operationalReserveUsd: number | null;
  latest: SnapshotRow | null;
  /** Bot self-reported health. Null on legacy/never-evaluated installs. */
  health: HealthRecord | null;
}

export interface HealthRecord {
  state: 'healthy' | 'degraded' | 'unknown';
  reasons: string[];
  since: number | null;
  lastAlertedAt: number | null;
}

export type UserRole = 'admin' | 'fund_manager' | 'investor';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

export interface DailyNavRow {
  date: string;
  firstTs: number;
  lastTs: number;
  firstNavUsd: number | null;
  lastNavUsd: number | null;
  changeUsd: number | null;
  changePct: number | null;
  // LP yield (Aerodrome side only, isolated from HL/funding/IL).
  lpValueStartUsd: number | null;
  lpValueEndUsd: number | null;
  feesAccruedUsd: number | null;
  aeroAccruedUsd: number | null;
  /** Trading-fee-only LP APR in percent (~0 for staked Aerodrome). */
  lpFeeApr: number | null;
  /** AERO-emissions LP APR in percent (the real yield for staked Aerodrome). */
  lpAeroApr: number | null;
  /** Total LP APR (fees + AERO emissions) in percent — Aerodrome-style. */
  lpTotalApr: number | null;
}

export interface IncomeSummary {
  aeroSwapUsd: number;
  aeroClaimedTokens: number;
  pendingSwapFeesUsd: number;
  gasCostUsd: number;
  /** Signed: + = received funding, − = paid funding. */
  fundingUsd: number;
  netIncomeUsd: number;
}

export interface RiskMetrics {
  windowDays: number;
  daySamples: number;
  peakNavUsd: number | null;
  currentNavUsd: number | null;
  maxDrawdownUsd: number | null;
  maxDrawdownPct: number | null;
  dailyVol: number | null;
  annualVol: number | null;
  sharpe: number | null;
  bestDayPct: number | null;
  worstDayPct: number | null;
}

/**
 * Build a `?strategyId=N` suffix if strategyId is set. Append-aware:
 *   - if base path already has a query string, returns '&strategyId=N'
 *   - else returns '?strategyId=N'
 *   - if strategyId is undefined, returns ''
 */
function withStrategy(strategyId: number | undefined, hasQuery: boolean): string {
  if (strategyId === undefined) return '';
  return `${hasQuery ? '&' : '?'}strategyId=${strategyId}`;
}

export const dashboardApi = {
  state: (strategyId?: number) =>
    api.get<State>(`/state${withStrategy(strategyId, false)}`),
  snapshots: (hours = 24, points = 500, strategyId?: number) =>
    api.get<SnapshotRow[]>(
      `/snapshots?hours=${hours}&points=${points}${withStrategy(strategyId, true)}`,
    ),
  actions: (limit = 50, strategyId?: number) =>
    api.get<ActionRow[]>(`/actions?limit=${limit}${withStrategy(strategyId, true)}`),
  /** Paginated + filterable variant of /api/actions. Returns rows for
   *  the requested page plus totalCount for the pager UI. */
  actionsPaged: (
    opts: {
      strategyId?: number;
      kind?: string;
      status?: string;
      fromTsMs?: number;
      toTsMs?: number;
      limit: number;
      offset: number;
    },
  ) => {
    const params = new URLSearchParams();
    params.set('limit', String(opts.limit));
    params.set('offset', String(opts.offset));
    if (opts.kind && opts.kind !== 'all') params.set('kind', opts.kind);
    if (opts.status && opts.status !== 'all') params.set('status', opts.status);
    if (opts.fromTsMs !== undefined) params.set('fromTs', String(opts.fromTsMs));
    if (opts.toTsMs !== undefined) params.set('toTs', String(opts.toTsMs));
    if (opts.strategyId !== undefined) params.set('strategyId', String(opts.strategyId));
    return api.get<{
      rows: ActionRow[];
      totalCount: number;
      limit: number;
      offset: number;
    }>(`/actions?${params.toString()}`);
  },
  counts: (strategyId?: number) =>
    api.get<Counts>(`/counts${withStrategy(strategyId, false)}`),
  daily: (days = 30, strategyId?: number) =>
    api.get<DailyNavRow[]>(`/daily?days=${days}${withStrategy(strategyId, true)}`),
  income: (strategyId?: number) =>
    api.get<IncomeSummary>(`/income${withStrategy(strategyId, false)}`),
  risk: (days = 30, strategyId?: number) =>
    api.get<RiskMetrics | null>(`/risk?days=${days}${withStrategy(strategyId, true)}`),
};

// ── Investor portfolio API (aHedge customer dashboard) ────────────────
// Curated, read-only, aggregated over active strategies (shared fund
// view). The ONLY surface the `investor` role can reach — see
// src/dashboard/portfolio.ts.

export type PortfolioRange = '24h' | '7d' | '30d' | 'all';

export interface PortfolioSummary {
  netValueUsd: number;
  deployedCapitalUsd: number;
  pnlUsd: number;
  pnlPct: number | null;
  annualizedRoiPct: number | null;
  strategyCount: number;
  positionsCount: number;
  daysRunning: number | null;
  inceptionTs: number | null;
  lastHedgeTs: number | null;
  /** Aggregate hedge-engine position across active strategies. */
  hedgeEngine: {
    lpValueUsd: number;
    shortNotionalUsd: number;
    netDeltaUsd: number;
  };
  serverTs: number;
}

export interface PortfolioNavPoint {
  ts: number;
  navUsd: number;
}

export interface PortfolioNav {
  range: PortfolioRange;
  sinceMs: number;
  bucketMs: number;
  deployedCapitalUsd: number;
  timeSeries: PortfolioNavPoint[];
}

export interface PortfolioLivePosition {
  strategyId: number;
  displayName: string;
  slug: string;
  venue: 'aerodrome' | 'uniswap_v3';
  baseSymbol: string;
  poolFee: number | null;
  rangePct: number;
  deployedCapitalUsd: number | null;
  netValueUsd: number | null;
  pnlUsd: number | null;
  pnlPct: number | null;
  annualizedRoiPct: number | null;
  lpValueUsd: number | null;
  shortNotionalUsd: number | null;
  netDeltaUsd: number | null;
  currentPrice: number | null;
  mintTickLower: number | null;
  mintTickUpper: number | null;
}

export interface PortfolioClosedPosition {
  strategyId: number;
  displayName: string;
  baseSymbol: string;
  venue: string;
  startTs: number;
  endTs: number | null;
  startValueUsd: number;
  endValueUsd: number | null;
  profitUsd: number | null;
  durationSec: number | null;
}

export interface PortfolioPositions {
  live: PortfolioLivePosition[];
  closed: PortfolioClosedPosition[];
}

export interface PortfolioPnl {
  /** LP swap fees + AERO rewards. */
  lpFeesRewardsUsd: number;
  /** HL funding + realized + unrealized. */
  hedgeResultsUsd: number;
  /** HL trading fees (subtracted in the tie-out). */
  hlFeesUsd: number;
  /** Balancing leg: price movement + IL + dust + residual. */
  marketIlUsd: number;
  /** = aggregate growth (net value − deployed). The four NAV-internal
   *  legs above tie to this. */
  totalPnlUsd: number;
  /** Gas paid from a reserve OUTSIDE strategy NAV — informational only,
   *  does NOT reduce investor capital. */
  gasUsd: number;
  netValueUsd: number;
  deployedCapitalUsd: number;
  annualizedRoiPct: number | null;
  components: {
    aeroSwapUsd: number;
    pendingSwapFeesUsd: number;
    fundingUsd: number;
    hlUnrealizedUsd: number;
    hlRealizedUsd: number;
  };
}

/** One option in the dashboard's strategy filter. */
export interface PortfolioStrategyOption {
  id: number;
  displayName: string;
  baseSymbol: string;
  venue: 'aerodrome' | 'uniswap_v3';
}

/** `?strategies=id,id` suffix. Empty/undefined ids → all active (omit). */
function stratParam(ids: number[] | undefined, hasQuery: boolean): string {
  if (!ids || ids.length === 0) return '';
  return `${hasQuery ? '&' : '?'}strategies=${ids.join(',')}`;
}

export const portfolioApi = {
  strategies: () => api.get<PortfolioStrategyOption[]>('/portfolio/strategies'),
  summary: (ids?: number[]) =>
    api.get<PortfolioSummary>(`/portfolio/summary${stratParam(ids, false)}`),
  nav: (range: PortfolioRange = '7d', ids?: number[]) =>
    api.get<PortfolioNav>(`/portfolio/nav?range=${range}${stratParam(ids, true)}`),
  positions: (ids?: number[]) =>
    api.get<PortfolioPositions>(`/portfolio/positions${stratParam(ids, false)}`),
  pnl: (ids?: number[]) => api.get<PortfolioPnl>(`/portfolio/pnl${stratParam(ids, false)}`),
};

// ── Strategy management (Phase 5 API surface) ──

export interface StrategyDetail {
  id: number;
  slug: string;
  displayName: string;
  status: 'active' | 'paused' | 'closed' | 'paper';
  mode: 'live' | 'paper';
  inceptionTs: number;
  closedTs: number | null;
  poolAddress: string;
  positionNftId: string | null;
  hlPerpCoin: string;
  rangePct: number;
  driftThresholdBps: number;
  hedgeCooldownSec: number;
  redeployCooldownSec: number;
  outOfRangeGraceSec: number;
  harvestCooldownSec: number;
  minHarvestValueUsd: number;
  hedgeSlippageBps: number;
  harvestSlippageBps: number;
  autoHedgeEnabled: boolean;
  autoRedeployEnabled: boolean;
  autoHarvestEnabled: boolean;
  /**
   * How the auto-hedger maintains the perp short across an epoch:
   *   'continuous' — drift-triggered rebalances (legacy, many trades)
   *   'static'     — open once at epoch start, freeze until next redeploy
   */
  hedgeModel: 'continuous' | 'static';
  /** Per-strategy opt-in for the atomic on-chain zap redeploy path. */
  useZapRedeploy: boolean;
  hlMarginTargetUsd: number | null;
  deployedCapitalUsd: number;
  /** Cumulative USD of refunded redeploy/deploy remainders, added to NAV. */
  dustUsd: number;
  /**
   * Lowercase EOA that owns this strategy's Base position + HL
   * account. Null = use bot's primary signer (backward compat).
   */
  walletAddress: string | null;
  configJson: Record<string, unknown>;
}

export interface CapitalEventRow {
  id: number;
  strategyId: number;
  kind: 'deposit' | 'topup' | 'withdraw' | 'close_realized';
  ts: number;
  amountUsd: number;
  ethPriceUsd: number | null;
  note: string | null;
}

export interface MintRequestBody {
  ethBudget: number;
  usdcBudget: number;
  rangePct?: number;
  slippageBps?: number;
}

export interface CapitalEventBody {
  amountUsd: number;
  ethPriceUsd?: number;
  note?: string;
}

// ── Path 2: deployment types + API ───────────────────────────────────

export type DeploymentStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed';

export type DeploymentMode = 'dry_run' | 'execute';

export interface DeploymentStepLogEntry {
  step: number;
  name: string;
  status: 'done' | 'failed' | 'running' | 'skipped';
  ts: number;
  txHash?: string;
  detail?: string;
  error?: string;
}

export interface StrategyDeployment {
  id: number;
  strategyId: number;
  status: DeploymentStatus;
  mode: DeploymentMode;
  budgetUsdc: number;
  allocationLpUsdc: number | null;
  allocationHlUsdc: number | null;
  allocationGasReserveUsdc: number | null;
  totalSteps: number;
  currentStep: number;
  currentStepName: string | null;
  stepLog: DeploymentStepLogEntry[];
  startedAt: number;
  completedAt: number | null;
  errorMessage: string | null;
  triggeredBy: string | null;
}

export interface LpEpochRow {
  id: number;
  strategyId: number;
  nftId: string;             // serialized bigint
  status: 'open' | 'closed';
  // Mint
  mintTs: number;
  mintTxHash: string | null;
  mintAmountWeth: number;    // human units
  mintAmountUsdc: number;
  mintEthPrice: number;
  mintTotalUsd: number;
  mintRangePct: number;
  /** Actual minted tick bounds — real band prices for the range bar
   *  (price = 1.0001^tick × 1e12 for the 18/6-decimals pools). */
  mintTickLower: number;
  mintTickUpper: number;
  // Close (null while open)
  closeTs: number | null;
  closeTxHash: string | null;
  closeAmountWeth: number | null;
  closeAmountUsdc: number | null;
  closeEthPrice: number | null;
  closeTotalUsd: number | null;
  feesCollectedUsd: number;
  aeroClaimedUsd: number;
  durationSec: number | null;
  gasTotalUsd: number;
  /** Legacy LP-only P&L from before migration 0014 — same as lpPnlUsd
   *  but only populated for closed epochs from the stored field. New
   *  consumers should use lpPnlUsd / strategyPnlUsd instead. */
  netPnlUsd: number | null;
  // Path 2 (migration 0014) — split LP vs full-strategy P&L.
  /** HL funding accrued during the epoch (signed). */
  hlFundingUsd: number | null;
  /** Realized P&L from hedge resizes during the epoch. */
  hlRealizedPnlUsd: number | null;
  /** Live HL unrealized P&L on the open perp position. For first epoch
   *  with no prior HL position this equals the change since mint; for
   *  multi-epoch strategies it would need a captured baseline. */
  hlUnrealizedPnlUsd: number | null;
  /** Total HL trading fees paid during the epoch (auto_hedge actions'
   *  attribution.feeUsdEst summed). Subtracted from strategyPnlUsd. */
  hlFeesUsd: number | null;
  /** LP-side P&L: close + fees + AERO − mint − gas. Computed live
   *  (mark-to-market) for open epochs. */
  lpPnlUsd: number | null;
  /** Strategy P&L: lpPnl + hlFunding + hlRealized + hlUnrealized.
   *  The honest delta-neutral cycle return — net of LP IL offsetting
   *  the short's MTM gain. */
  strategyPnlUsd: number | null;
  /** Live MTM LP value for open epochs (lp_eth × eth_price + lp_usdc
   *  from the latest snapshot). Null for closed epochs — use
   *  closeTotalUsd instead. The table renders one or the other in the
   *  same column so the operator sees the current/final LP value
   *  side-by-side with the mint value. */
  currentLpValueUsd: number | null;
}

export const deploymentsApi = {
  create: (
    strategyId: number,
    body: { budgetUsdc: number; hlTargetUsdc?: number; mode?: DeploymentMode },
  ) =>
    api.post<StrategyDeployment>(`/strategies/${strategyId}/deployments`, body),
  active: (strategyId: number) =>
    api.get<StrategyDeployment>(`/strategies/${strategyId}/deployments/active`),
  get: (id: number) => api.get<StrategyDeployment>(`/deployments/${id}`),
  execute: (id: number) =>
    api.post<StrategyDeployment>(`/deployments/${id}/execute`),
  retry: (id: number) =>
    api.post<StrategyDeployment>(`/deployments/${id}/retry`),
  /** Rerun dry-run with a possibly different budget. Only valid when
   *  mode='dry_run'. Omit budgetUsdc to reset state only. */
  reset: (id: number, body?: { budgetUsdc?: number }) =>
    api.post<StrategyDeployment>(`/deployments/${id}/reset`, body ?? {}),
};

export const epochsApi = {
  list: (strategyId: number, limit = 50, offset = 0) =>
    api.get<LpEpochRow[]>(
      `/strategies/${strategyId}/epochs?limit=${limit}&offset=${offset}`,
    ),
};

export const strategiesApi = {
  list: () => api.get<StrategyDetail[]>('/strategies'),
  get: (id: number) => api.get<StrategyDetail>(`/strategies/${id}`),
  create: (body: Partial<StrategyDetail> & { slug: string; displayName: string; poolAddress: string; rangePct: number; deployedCapitalUsd: number }) =>
    api.post<StrategyDetail>('/strategies', body),
  update: (id: number, body: Partial<StrategyDetail>) =>
    api.patch<StrategyDetail>(`/strategies/${id}`, body),
  pause: (id: number) => api.post<StrategyDetail>(`/strategies/${id}/pause`),
  resume: (id: number) => api.post<StrategyDetail>(`/strategies/${id}/resume`),
  close: (id: number) => api.post<StrategyDetail>(`/strategies/${id}/close`),

  // Capital ledger
  capitalEvents: (id: number) =>
    api.get<CapitalEventRow[]>(`/strategies/${id}/capital-events`),
  deposit: (id: number, body: CapitalEventBody) =>
    api.post<{ id: number; deployedCapitalUsd: number }>(`/strategies/${id}/deposit`, body),
  topup: (id: number, body: CapitalEventBody) =>
    api.post<{ id: number; deployedCapitalUsd: number }>(`/strategies/${id}/topup`, body),
  withdraw: (id: number, body: CapitalEventBody) =>
    api.post<{ id: number; deployedCapitalUsd: number }>(`/strategies/${id}/withdraw`, body),

  // Operations — bot loop picks up on next tick
  mint: (id: number, body: MintRequestBody) =>
    api.post<{ queued: boolean; op: string }>(`/strategies/${id}/mint`, body),
  redeploy: (id: number) =>
    api.post<{ queued: boolean; op: string }>(`/strategies/${id}/redeploy`),
  harvest: (id: number) =>
    api.post<{ queued: boolean; op: string }>(`/strategies/${id}/harvest`),
  hedge: (id: number) =>
    api.post<{ queued: boolean; op: string }>(`/strategies/${id}/hedge`),
};

export const authApi = {
  me: () => api.get<CurrentUser>('/auth/me'),
};

// ── Wallets registry (migration 0017) ─────────────────────────────────────

export type WalletHealth = 'ok' | 'mismatch' | 'no_signer';
export type WalletKeyEnvRef = 'wallet_id' | 'legacy_primary' | 'legacy_secondary';

export interface WalletView {
  id: number;
  label: string;
  address: string;
  keyEnvRef: WalletKeyEnvRef;
  enabled: boolean;
  notes: string | null;
  health: WalletHealth;
  resolvedAddress: string | null;
  signerMode: 'single' | 'split' | null;
  balances: { eth: string; weth: string; usdc: string } | null;
  strategyCount: number;
}

export interface UnregisteredSigner {
  address: string;
  suggestedKeyEnvRef: WalletKeyEnvRef;
  suggestedWalletId: number | null;
}

export interface WalletsResponse {
  wallets: WalletView[];
  unregistered: UnregisteredSigner[];
}

export interface CreateWalletBody {
  label: string;
  address: string;
  keyEnvRef?: WalletKeyEnvRef;
  notes?: string | null;
}

export const walletsApi = {
  list: () => api.get<WalletsResponse>('/wallets'),
  create: (body: CreateWalletBody) =>
    api.post<{ wallet: WalletView; nextStep: string | null }>('/wallets', body),
  update: (
    id: number,
    body: { label?: string; enabled?: boolean; notes?: string | null },
  ) => api.patch<WalletView>(`/wallets/${id}`, body),
};
